// Degenesis cabinet hub. Through the seven-day Degenesis sale there is no daily
// drawing to watch, so the four quadrants of the Daily Drawing board become
// four buy buttons: Tickets, Luckboxes, Passes and Degenesis boxes.
//
// The purchase desk stays the transaction owner for tickets and Luckboxes: the
// popup shows the desk's own artwork and every click drives the desk's own
// controls, so pricing, funding, boons and the buy path are never duplicated.
// Passes open the existing AFKING PASSES popup. Only the Degenesis box has its
// own write (lootbox.js purchaseDegenesisBox).
//
// Window (DegenerusGameDegenesisModule + GameTimeLib): day 1 is the deploy day,
// Degenesis days 1-7 are game days 2-8, and the request that opens game day 9
// rolls the first real daily jackpot. The hub shows from deploy until that day
// change; the Daily Drawing takes over as the first jackpot runs.

import { boonTypePresentation } from '../app/boons.js';
import { get, subscribe, getActingAddress } from '../app/store.js';
import { ETH_DIVISOR } from '../app/chain-config.js';
import { displayEth, displayToken, trimDisplayZeros } from '../app/scaling.js';
import { lock, unlock } from '../app/scroll-lock.js';
import { TX_CONFIRMED_EVENT } from '../app/contracts.js';
import { dgnBadgePath, dgnTicketAccent } from '../app/dgn-traits.js';
import {
  DEGENESIS_START_DAY, MAIN_LAUNCH_DAY, degenesisDay, degenesisWallDay, secondsToNextDay,
  degenesisWindow, formatDegenesisCountdown, ethTextToWei,
  degenesisNow,
} from '../app/degenesis.js';

// The timeline helpers live in app/degenesis.js so the purchase desk shares them.
export { DEGENESIS_START_DAY, MAIN_LAUNCH_DAY, degenesisDay, degenesisWallDay, secondsToNextDay };
/** The machine shows the Daily Drawing again from this day on. */
export const DEGENESIS_LAYOUT_END_DAY = MAIN_LAUNCH_DAY;

const CREDIT_POLL_MS = 30_000;
const DESK_TIMEOUT_MS = 8_000;
const DEGENESIS_BOX_ART = '/app/assets/lootbox/degenesis-box-v2-classic-logo.webp?v=1';
const PASSES_ART = '/app/assets/afking-passes-teal-ember-v18.svg';
const LUCKBOX_ART = '/app/assets/lootbox/degenerus-lootbox-case-large-v36-buy-in-card.webp';

/**
 * The hub's whole presentation state.
 * @param {{supported:boolean, day:number|null, creditWei:bigint|null, minWei:bigint, connected:boolean}} input
 */
export function degenesisHubModel({ supported, day, creditWei, minWei, connected, gameOver = false, degenesisClosed = 0 }) {
  const active = !gameOver && supported === true && Number.isInteger(day) && day < DEGENESIS_LAYOUT_END_DAY;
  const saleOpen = !gameOver && degenesisClosed < 7 && Number.isInteger(day) && day >= DEGENESIS_START_DAY && day < MAIN_LAUNCH_DAY;
  const credit = typeof creditWei === 'bigint' ? creditWei : null;
  let lockReason = null;
  if (!saleOpen) lockReason = 'opens-tomorrow';
  else if (!connected) lockReason = 'connect';
  else if (credit == null || credit < minWei) lockReason = 'no-credit';
  return {
    active,
    saleOpen,
    degenesisDay: saleOpen ? day - DEGENESIS_START_DAY + 1 : null,
    creditWei: credit,
    unlocked: active && lockReason == null,
    lock: lockReason,
  };
}

/** Floor a raw credit amount to the Degenesis unit. */
export function degenesisMaxWei(creditWei, unitWei) {
  const credit = BigInt(creditWei ?? 0n);
  const unit = BigInt(unitWei);
  return unit > 0n ? credit - (credit % unit) : 0n;
}

/**
 * Display-ETH text (mainnet-equivalent) → raw wei; null when invalid or not a whole Degenesis
 * unit. purchaseDegenesisBox reverts a non-unit amount, so the form rejects it instead of trimming.
 */
export function degenesisAmountWei(text, unitWei, divisor = ETH_DIVISOR) {
  const scaled = ethTextToWei(text, divisor);
  const unit = BigInt(unitWei);
  if (scaled == null || unit <= 0n || scaled % unit !== 0n) return null;
  return scaled;
}

/** Exact launch-level boon draw count from DegenerusGameBoonModule.sol:490-507.
 * This previews rolls: duplicate/weaker or unavailable boons may not be kept.
 */
export function degenesisBoonOdds(amountWei, divisor = ETH_DIVISOR) {
  if (amountWei == null || BigInt(amountWei) <= 0n) return null;
  const eth = 10n ** 18n / BigInt(divisor);
  // _boonAvgMaxValue(1): priceForLevel(0)=.01 ETH, lazy levels 2..11=.21 ETH.
  const average = (1568n * eth + 4182n * (eth / 100n) + 6n * (21n * eth / 100n)) / 2856n;
  const expected = average * 5000n / 10000n;
  let budget = BigInt(amountWei) / 5n;
  if (budget > eth) budget = eth;
  let chance = budget * 1000000n / expected;
  if (chance > 3000000n) chance = 3000000n;
  return { guaranteed: Number(chance / 1000000n), extraPpm: Number(chance % 1000000n) };
}

/** Expected boon rolls as one percentage: 1 guaranteed + 12.5% of another reads 112.5%. */
export function degenesisBoonChanceLabel(odds) {
  if (!odds) return '—';
  const hundredths = odds.guaranteed * 10000 + Math.floor(odds.extraPpm / 100);
  return `${(hundredths / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

/** One order's result line: "FLIP 1,234 · 2 HIGH PASSES · WWXRP 1", or the pending note. */
export function degenesisOrderSummary(order, { flip = (v) => String(v), dgnrs = (v) => String(v), wwxrp = (v) => String(v) } = {}) {
  const r = order?.resolved;
  if (!r) return 'Opens after its cohort settles';
  const parts = [];
  if (BigInt(r.flip ?? 0) > 0n) parts.push(`${flip(r.flip)} FLIP`);
  if (Number(r.highPasses) > 0) parts.push(`${r.highPasses} HIGH ${Number(r.highPasses) === 1 ? 'PASS' : 'PASSES'}`);
  if (Number(r.normalPasses) > 0) parts.push(`${r.normalPasses} ${Number(r.normalPasses) === 1 ? 'PASS' : 'PASSES'}`);
  if (BigInt(r.dgnrs ?? 0) > 0n) parts.push(`${dgnrs(r.dgnrs)} sDGNRS`);
  if (BigInt(r.wwxrp ?? 0) > 0n) parts.push(`${wwxrp(r.wwxrp)} WWXRP`);
  if (r.boonTypes?.length) parts.push(`Rolled: ${r.boonTypes.map(type => { const b = boonTypePresentation(type); return `${b.name} ${b.effect}`.trim(); }).join(', ')}`);
  return parts.join(' · ') || 'Settled';
}

const ethText = (wei, digits = 4) => trimDisplayZeros(displayEth(BigInt(wei ?? 0n), digits));

/** Keep popup ETH amounts exact, with one readable spelling in prices and CTA. */
export function purchasePopupQuote(value) {
  return String(value ?? '').replace(/\d*\.\d+(?=\s*ETH\b)/g, amount => (
    trimDisplayZeros(amount.startsWith('.') ? `0${amount}` : amount)
  ));
}

const LOCK_COPY = {
  'opens-tomorrow': 'OPENS TOMORROW',
  connect: 'CONNECT WALLET',
  'no-credit': 'NEED CREDIT',
};

const MODULES = [
  ['tickets', 'TICKETS'],
  ['luckboxes', 'LUCKBOXES'],
  ['passes', 'PASSES'],
  ['degenesis', 'DEGENESIS'],
];

class AppDegenesisHub extends HTMLElement {
  #unsubs = [];
  #dayTimer = null;
  #creditTimer = null;
  #machine = null;
  #buttons = new Map();
  #lootbox = null;
  #model = degenesisHubModel({ supported: false, day: null, creditWei: null, minWei: 0n, connected: false });
  #creditWei = null;
  #creditOwner = null;
  #dialogKind = null;
  #returnFocus = null;
  #deskObserver = null;
  #boxEditorKind = 'custom';
  #busy = false;
  #review = false;
  #dialogEl = null;
  #receiptEl = null;
  #receiptReturnFocus = null;
  #deskBuyPending = false;
  #onTxConfirmed = () => {
    void this.#refreshCredit();
    // The desk owns the purchase write, so its confirmation is the only signal
    // that the order this popup sent went through. Close like a finished buy.
    if (this.#deskBuyPending && (this.#dialogKind === 'tickets' || this.#dialogKind === 'luckboxes')) {
      this.#close();
    }
  };

  connectedCallback() {
    if (this.hasAttribute('data-mounted')) return;
    this.setAttribute('data-mounted', '');
    this.#machine = this.closest('.jackpot-hero__machine');
    this.innerHTML = `
      <div class="dgx-bank" data-bind="dgx-bank" role="group" aria-label="Degenesis buy options">
        ${MODULES.map(([kind, label]) => `
          <button type="button" class="dgx-module dgx-module--${kind}" data-dgx-kind="${kind}"
                  ${kind === 'passes'
                    ? 'data-panel-open="passes" aria-controls="panel-passes" aria-haspopup="dialog" aria-expanded="false"'
                    : 'aria-haspopup="dialog"'}>
            <span class="dgx-module__art" aria-hidden="true">${this.#moduleArt(kind)}</span>
            <span class="dgx-module__label">${label}</span>
            ${kind === 'degenesis' ? '<span class="dgx-module__lock" data-bind="dgx-lock"></span>' : ''}
          </button>`).join('')}
      </div>
      <div class="qst-action-dialog dgx-dialog" data-bind="dgx-dialog" hidden
           role="dialog" aria-modal="true" aria-labelledby="dgx-dialog-title">
        <button type="button" class="qst-action-dialog__backdrop" data-bind="dgx-close"
                aria-label="Close"></button>
        <section class="qst-action-dialog__card dgx-dialog__card" data-bind="dgx-card">
          <header class="qst-action-dialog__hero">
            <button type="button" class="qst-action-dialog__close" data-bind="dgx-close"
                    aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                   stroke-linecap="round" aria-hidden="true" focusable="false">
                <path d="m6 6 12 12M18 6 6 18"/>
              </svg>
            </button>
            <span class="qst-action-dialog__sigil" aria-hidden="true">
              <img data-bind="dgx-sigil" alt="">
            </span>
            <span class="qst-action-dialog__heading">
              <span class="qst-action-dialog__eyebrow" data-bind="dgx-eyebrow"></span>
              <h3 id="dgx-dialog-title" data-bind="dgx-title"></h3>
              <span class="qst-action-dialog__copy" data-bind="dgx-copy"></span>
            </span>
          </header>
          <div class="dgx-dialog__body" data-bind="dgx-body"></div>
          <span class="qst-action-dialog__state" data-bind="dgx-state" aria-live="polite" hidden></span>
          <div class="dgx-dialog__actions">
            <button type="button" class="dgx-dialog__clear" data-bind="dgx-clear" hidden>CLEAR</button>
            <button type="button" class="qst-action-confirm dgx-dialog__confirm" data-write
                    data-bind="dgx-confirm">BUY</button>
          </div>
        </section>
      </div>
    `;

    this.#dialogEl = HTMLElement.prototype.querySelector.call(this, '[data-bind="dgx-dialog"]');
    if (this.#dialogEl) document.body.appendChild(this.#dialogEl);

    for (const button of this.#qa('[data-dgx-kind]')) {
      const kind = button.getAttribute('data-dgx-kind');
      this.#buttons.set(kind, button);
      if (kind === 'passes') continue; // the shared panel-popup delegate owns it
      button.addEventListener('click', () => { void this.#open(kind, button); });
    }
    for (const close of this.#qa('[data-bind="dgx-close"]')) {
      close.addEventListener('click', () => this.#close());
    }
    this.#dialog()?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#close();
    });
    this.#q('[data-bind="dgx-confirm"]')?.addEventListener('click', () => { void this.#confirm(); });
    this.#q('[data-bind="dgx-clear"]')?.addEventListener('click', () => {
      this.#boxEditorKind = 'custom';
      this.#deskControl('dec-ticket-clear')?.click();
      this.#syncFromDesk();
    });

    this.#unsubs.push(
      subscribe('connected.address', () => { void this.#refreshCredit(); }),
      subscribe('viewing.address', () => { void this.#refreshCredit(); }),
      subscribe('ui.mode', () => { void this.#refreshCredit(); }),
      subscribe('app.gameState', () => { void this.#evaluate(); }),
      subscribe('app.daySync', () => { void this.#evaluate(); }),
    );
    document.addEventListener(TX_CONFIRMED_EVENT, this.#onTxConfirmed);
    void this.#evaluate();
    // The replay module can upgrade after this hub; seat the buttons as soon
    // as its original board shell exists, regardless of module load order.
    customElements.whenDefined('replay-panel').then(() => {
      if (this.hasAttribute('data-mounted')) this.#render();
    });
  }

  disconnectedCallback() {
    for (const unsubscribe of this.#unsubs) {
      try { unsubscribe(); } catch (_error) { /* one teardown cannot block another */ }
    }
    this.#unsubs = [];
    document.removeEventListener(TX_CONFIRMED_EVENT, this.#onTxConfirmed);
    clearTimeout(this.#dayTimer);
    clearInterval(this.#creditTimer);
    this.#creditTimer = null;
    this.#deskObserver?.disconnect();
    this.#deskObserver = null;
    if (this.#dialogKind) unlock();
    this.#dialogKind = null;
    this.#setMachineFlag(false);
    this.#machine = null;
    this.#buttons.clear();
    this.#dialogEl?.remove();
    this.#dialogEl = null;
    this.#receiptEl?.close();
    this.#receiptEl?.remove();
    this.#receiptEl = null;
    this.removeAttribute('data-mounted');
  }

  #moduleArt(kind) {
    if (kind === 'tickets') {
      // The desk's TICKET face (purchase-desk.css) with one random sample:
      // a symbol per quadrant, four different colours, accent from the traits.
      const colours = [0, 1, 2, 3, 4, 5, 6, 7].sort(() => Math.random() - 0.5);
      const traits = [0, 1, 2, 3].map((q) => ({ q, sym: Math.floor(Math.random() * 8), col: colours[q] }));
      const accent = dgnTicketAccent(traits.map(({ q, sym, col }) => (q << 6) | (col << 3) | sym)).hex;
      return `<span class="app-decimator-panel dgx-ticket-art">
        <span class="dec-ticket-face" style="--ticket-line-color: ${accent}">
          ${traits.map(({ q, sym, col }) => `<span class="dec-ticket-trait"><img src="${dgnBadgePath(q, sym, col)}" alt=""></span>`).join('')}
          <span class="dec-ticket-center"><img src="/whitepaper/flame-center.svg" alt=""></span>
        </span>
      </span>`;
    }
    if (kind === 'luckboxes') {
      // Keep the badge registered to the square artwork at every cabinet size.
      // The source render has a tiny flame; use the shared vector mark instead.
      return `<svg class="dgx-luckbox-art" viewBox="0 65 600 440" aria-hidden="true">
        <image href="${LUCKBOX_ART}" width="600" height="600" />
        <circle cx="300" cy="394" r="19" fill="#fafafa" />
        <image href="/whitepaper/flame-center.svg" x="286" y="376" width="28" height="36" />
      </svg>`;
    }
    const src = kind === 'passes' ? PASSES_ART : DEGENESIS_BOX_ART;
    return `<img src="${src}" alt="" loading="lazy" decoding="async">`;
  }

  // ---- State -----------------------------------------------------------------

  async #evaluate() {
    clearTimeout(this.#dayTimer);
    this.#review = false;
    const now = degenesisNow();
    const day = degenesisDay();
    // Layout follows the deployment clock synchronously. A slow or failed
    // purchase-module load must never expose the normal jackpot underneath.
    this.#model = this.#buildModel(true, day);
    this.#render();
    if (this.#model.active) {
      try {
        this.#lootbox ??= await import('../app/lootbox.js');
      } catch (error) {
        console.warn?.('[degenesis] purchase module unavailable', error);
      }
    }
    if (!this.hasAttribute('data-mounted')) return;
    if (this.#model.active) {
      if (!this.#creditTimer) {
        this.#creditTimer = setInterval(() => {
          if (document.visibilityState !== 'hidden') void this.#refreshCredit();
        }, CREDIT_POLL_MS);
      }
      void this.#refreshCredit();
    } else if (this.#creditTimer) {
      clearInterval(this.#creditTimer);
      this.#creditTimer = null;
    }
    const wait = secondsToNextDay(now);
    if (wait != null && day != null && day < DEGENESIS_LAYOUT_END_DAY) {
      this.#dayTimer = setTimeout(() => { void this.#evaluate(); }, (wait + 1) * 1000);
    }
  }

  /**
   * Browser review only: pin the hub to a Degenesis day and credit on a
   * deployment that has no sale. Every later evaluation replaces it.
   */
  async setReviewState({ day = 3, creditWei = 0n } = {}) {
    this.#lootbox ??= await import('../app/lootbox.js');
    this.#review = true;
    this.#creditOwner = this.#owner();
    this.#creditWei = BigInt(creditWei);
    this.#model = this.#buildModel(true, day);
    this.#render();
  }

  #buildModel(supported, day) {
    return degenesisHubModel({
      supported,
      day,
      gameOver: get('app.gameState')?.gameOver === true,
      degenesisClosed: Number(get('app.gameState')?.degenesisClosed ?? 0),
      creditWei: this.#creditOwner === this.#owner() ? this.#creditWei : null,
      minWei: this.#lootbox?.DEGENESIS_MIN_WEI ?? 0n,
      connected: Boolean(this.#owner()),
    });
  }

  #owner() {
    if (get('ui.mode') === 'combined') return null;
    const acting = getActingAddress();
    return acting ? String(acting).toLowerCase() : null;
  }

  async #refreshCredit() {
    if (!this.#model.active || !this.#lootbox || this.#review) return;
    const owner = this.#owner();
    let credit = null;
    if (owner) {
      try { credit = await this.#lootbox.readDegenesisCredit({ player: owner }); }
      catch (error) { console.warn?.('[degenesis] credit read failed', error); }
    }
    if (owner !== this.#owner()) return; // the account moved while the read was in flight
    this.#creditOwner = owner;
    this.#creditWei = credit;
    const day = degenesisDay();
    this.#model = this.#buildModel(true, day);
    this.#render();
    if (this.#dialogKind === 'degenesis') this.#renderDegenesisBody({ keepAmount: true });
  }

  // ---- Bank ------------------------------------------------------------------

  #render() {
    const { active, unlocked, lock: lockReason } = this.#model;
    this.toggleAttribute('data-active', active);
    this.#setMachineFlag(active);
    const degenesis = this.#q('[data-dgx-kind="degenesis"]');
    if (degenesis) {
      degenesis.classList.toggle('is-locked', !unlocked);
      degenesis.setAttribute('aria-label', unlocked
        ? `Buy Degenesis boxes, ${ethText(this.#model.creditWei)} ETH credit`
        : `Degenesis boxes locked: ${(LOCK_COPY[lockReason] || 'locked').toLowerCase()}`);
    }
    const lockEl = this.#q('[data-bind="dgx-lock"]');
    if (lockEl) lockEl.textContent = unlocked ? `${ethText(this.#model.creditWei)} ETH` : (LOCK_COPY[lockReason] || '');
    if (!active && this.#dialogKind) this.#close({ restoreFocus: false });
  }

  #setMachineFlag(on) {
    const machine = this.#machine;
    if (!machine) return;
    machine.toggleAttribute('data-degenesis', on);
    const replay = machine.querySelector('replay-panel');
    const bank = HTMLElement.prototype.querySelector.call(this, '.dgx-bank');
    // Move the same controls back before a normal board rebuild removes them.
    if (!on) for (const button of this.#buttons.values()) bank?.appendChild(button);
    replay?.setDegenesisMode?.(on);
    replay?.toggleAttribute('data-degenesis', on);
    if (!on) return;
    const positions = { tickets: 'tl', passes: 'tr', luckboxes: 'bl', degenesis: 'br' };
    for (const [kind, button] of this.#buttons) {
      const quadrant = replay?.querySelector(`.replay-tq[data-pos="${positions[kind]}"]`);
      if (quadrant && button.parentElement !== quadrant) quadrant.replaceChildren(button);
    }
    const control = replay?.querySelector('[data-bind="reveal-btn"]');
    if (control) {
      control.textContent = 'DEGENESIS';
      control.title = this.#model.saleOpen
        ? `Degenesis day ${this.#model.degenesisDay} of 7. Choose a purchase above.`
        : 'Earn credit today. Degenesis opens tomorrow.';
    }
  }

  // ---- Dialog ----------------------------------------------------------------

  #dialog() { return this.#dialogEl; }

  // The dialog lives on <body>: the machine is a containing block for fixed
  // descendants, and its neighbours would paint over a backdrop left inside it.
  #q(selector) { return (this.#machine || this).querySelector(selector) || this.#dialogEl?.querySelector(selector) || null; }
  #qa(selector) {
    return [...(this.#machine || this).querySelectorAll(selector), ...(this.#dialogEl?.querySelectorAll(selector) || [])];
  }

  async #open(kind, trigger) {
    if (!this.#model.active || this.#dialogKind) return;
    const dialog = this.#dialog();
    if (!dialog) return;
    if (kind === 'tickets' || kind === 'luckboxes') {
      const ready = await this.#desk();
      if (!ready) {
        console.warn?.('[degenesis] purchase desk is not ready');
        return;
      }
    }
    this.#dialogKind = kind;
    this.#returnFocus = trigger || null;
    trigger?.setAttribute('aria-expanded', 'true');
    this.#q('[data-bind="dgx-card"]')?.setAttribute('data-kind', kind);
    this.#setState('');
    if (kind === 'degenesis') this.#renderDegenesisBody();
    else this.#renderDeskBody(kind);
    dialog.hidden = false;
    lock();
    try { this.#q('[data-bind="dgx-close"].qst-action-dialog__close')?.focus({ preventScroll: true }); }
    catch (_e) { /* focus is progressive enhancement */ }
    if (kind === 'degenesis') void this.#refreshCredit();
  }

  #close({ restoreFocus = true } = {}) {
    if (!this.#dialogKind || this.#busy) return;
    const dialog = this.#dialog();
    if (dialog) dialog.hidden = true;
    this.#deskObserver?.disconnect();
    this.#deskObserver = null;
    this.#dialogKind = null;
    this.#deskBuyPending = false;
    const returnFocus = this.#returnFocus;
    this.#returnFocus = null;
    returnFocus?.setAttribute('aria-expanded', 'false');
    unlock();
    if (restoreFocus) {
      try { returnFocus?.focus({ preventScroll: true }); }
      catch (_e) { /* focus is progressive enhancement */ }
    }
  }

  #setHero({ eyebrow, title, copy, sigil }) {
    const day = this.#model.degenesisDay;
    const left = degenesisWindow(Date.now() / 1000).secondsLeft;
    // Non-breaking spaces keep "CLOSES IN 6m 15s" on one line on a phone.
    const clock = left == null ? '' : ` · ${formatDegenesisCountdown(left).replaceAll(' ', '\u00a0')}`;
    this.#q('[data-bind="dgx-eyebrow"]').textContent = eyebrow
      ?? (day ? `DEGENESIS · DAY\u00a0${day}\u00a0OF\u00a07${clock ? ` · CLOSES\u00a0IN\u00a0${clock.slice(3)}` : ''}`
        : `DEGENESIS · OPENS\u00a0IN${clock ? `\u00a0${clock.slice(3)}` : '\u00a0A\u00a0DAY'}`);
    this.#q('[data-bind="dgx-title"]').textContent = title;
    this.#q('[data-bind="dgx-copy"]').textContent = copy;
    const img = this.#q('[data-bind="dgx-sigil"]');
    if (img) img.src = sigil;
  }

  #setState(text, tone = '') {
    const state = this.#q('[data-bind="dgx-state"]');
    const card = this.#q('[data-bind="dgx-card"]');
    if (state) {
      state.textContent = text || '';
      state.hidden = !text;
    }
    if (card) {
      if (tone) card.setAttribute('data-state', tone);
      else card.removeAttribute('data-state');
    }
  }

  async #confirm() {
    if (this.#dialogKind === 'degenesis') await this.#buyDegenesis();
    else if (this.#dialogKind) {
      this.#deskBuyPending = true;
      this.#deskControl('dec-buy-cta')?.click();
    }
  }

  // ---- Tickets / Luckboxes: the desk's own artwork, the desk's own order ------

  #deskRoot() { return document.querySelector('app-decimator-panel'); }

  #deskControl(bind) {
    return this.#deskRoot()?.querySelector(`[data-bind="${bind}"]`) || null;
  }

  async #desk() {
    if (this.#deskControl('dec-buy-cta')) return true;
    try {
      await import('./app-decimator-panel.js');
      const deadline = Date.now() + DESK_TIMEOUT_MS;
      while (!this.#deskControl('dec-buy-cta') && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.warn?.('[degenesis] purchase desk failed to load', error);
    }
    return Boolean(this.#deskControl('dec-buy-cta'));
  }

  #renderDeskBody(kind) {
    const tickets = kind === 'tickets';
    this.#boxEditorKind = 'custom';
    // Touch screens have no right-click; Chrome and Safari fire the same
    // contextmenu event on a long-press, which the desk already handles.
    const touch = globalThis.matchMedia?.('(hover: none)')?.matches === true;
    const [add, remove] = touch ? ['Tap', 'Long-press'] : ['Click', 'Right-click'];
    this.#setHero({
      title: tickets ? 'TICKETS' : 'LUCKBOXES',
      copy: tickets
        ? `${add} a piece to add it to your order. ${remove} removes one.`
        : `${add} a Luckbox to add it to your order. ${remove} removes one.`,
      sigil: tickets ? '/whitepaper/flame-center.svg' : LUCKBOX_ART,
    });
    const body = this.#q('[data-bind="dgx-body"]');
    body.innerHTML = `
      ${tickets ? `
        <div class="dgx-payment" data-bind="dgx-payment" hidden>
          <span>PAY WITH</span>
          <div class="dgx-payment__choices" role="group" aria-label="Ticket payment currency">
            <button type="button" data-dgx-currency="eth" aria-pressed="true">
              <img src="/badges-circular/crypto_06_ethereum_green.svg" alt="">ETH
            </button>
            <button type="button" data-dgx-currency="flip" aria-pressed="false">
              <img src="/whitepaper/flame-logo-split.svg" alt="">FLIP
            </button>
          </div>
        </div>` : ''}
      <div class="app-decimator-panel dgx-shelf dgx-shelf--${kind}">
        <div class="${tickets ? 'dec-ticket-pieces' : 'dec-box-grid'}" data-bind="dgx-shelf"></div>
      </div>
      ${tickets ? '' : `
        <div class="dgx-custom">
          <span class="dgx-custom__label" data-bind="dgx-box-kind">CUSTOM</span>
          <span class="qst-action-dgn__stepper qst-action-adjust__stepper">
            <button type="button" data-dgx-custom-step="-1" aria-label="Remove one custom Luckbox">−</button>
            <input type="number" data-bind="dgx-custom-count" min="0" max="100" step="1"
                   inputmode="numeric" aria-label="Custom Luckbox quantity">
            <strong>BOXES</strong>
            <button type="button" data-dgx-custom-step="1" aria-label="Add one custom Luckbox">+</button>
          </span>
          <span class="qst-action-dgn__stepper qst-action-adjust__stepper dgx-custom__size">
            <input type="number" data-bind="dgx-custom-eth" min="0.01" step="0.01"
                   inputmode="decimal" aria-label="Custom Luckbox ETH per box">
            <strong>ETH EACH</strong>
          </span>
        </div>`}
      <div class="dgx-order" data-bind="dgx-order"></div>
      <div class="dgx-bonus" aria-live="polite"><small>PURCHASE BONUS</small><strong data-bind="dgx-bonus">+0 FLIP</strong></div>
      <p class="dgx-reward dgx-reward--boon" data-bind="dgx-boon-effect" role="status" hidden></p>
      <p class="dgx-reward dgx-reward--quest" data-bind="dgx-quest-effect" role="status" hidden></p>
    `;
    for (const button of body.querySelectorAll('[data-dgx-currency]')) {
      button.addEventListener('click', () => {
        const control = this.#deskControl('dec-funds-total-flip');
        if (button.disabled || !control || control.hidden || control.disabled) return;
        const flip = button.getAttribute('data-dgx-currency') === 'flip';
        if ((control.getAttribute('aria-pressed') === 'true') !== flip) control.click();
        this.#syncFromDesk();
      });
    }
    if (!tickets) this.#wireCustomBoxes(body);
    this.#q('[data-bind="dgx-clear"]').hidden = false;
    this.#buildShelf(kind);
    this.#syncFromDesk();

    // The desk relabels its own Buy button on every order change, while a
    // purchase is in flight, and after it settles; mirroring that one node
    // keeps this popup on the desk's exact state without a second model.
    this.#deskObserver?.disconnect();
    const watched = [
      this.#deskControl('dec-buy-cta'),
      this.#deskControl('dec-flip-credit'),
      this.#deskControl('dec-purchase-boon-effect'),
      this.#deskControl('dec-purchase-quest-effect'),
      this.#deskControl('dec-custom-box-bonus'),
      this.#deskControl('dec-funds-total-flip'),
      ...['entry', 'ticket', 'pack'].map(kind => this.#deskControl(`dec-${kind}-price`)),
      this.#deskControl('dec-error'),
      this.#deskControl('dec-foil-row'),
      this.#deskControl('dec-ticket-sample'),
      ...['small', 'medium', 'large'].map(size => this.#deskControl(`dec-box-price-${size}`)),
    ].filter(Boolean);
    this.#deskObserver = new MutationObserver(() => this.#syncFromDesk());
    for (const node of watched) {
      this.#deskObserver.observe(node, { subtree: true, childList: true, characterData: true, attributes: true });
    }
  }

  #shelfSources(kind) {
    if (kind === 'tickets') {
      return [
        ['dec-ticket-add-entry', 'entry'],
        ['dec-ticket-add-ticket', 'ticket'],
        ['dec-ticket-add-pack', 'pack'],
        ['dec-foil-row', 'foil'],
      ];
    }
    return [
      ['dec-box-add-small', 'small'],
      ['dec-box-add-medium', 'medium'],
      ['dec-box-add-large', 'large'],
    ];
  }

  #buildShelf(kind) {
    const shelf = this.#q('[data-bind="dgx-shelf"]');
    if (!shelf) return;
    shelf.textContent = '';
    for (const [bind, piece] of this.#shelfSources(kind)) {
      const source = this.#deskControl(bind);
      if (!source) continue;
      if (piece === 'foil' && source.hidden) continue;
      const host = kind === 'tickets' ? source : source.closest('.dec-box-card');
      if (!host) continue;
      const clone = host.cloneNode(true);
      // cloneNode does not copy the desk's load listeners. The popup owns
      // these images, including cold loads while the source desk is hidden.
      for (const image of clone.querySelectorAll('.dec-box-card__image')) {
        const price = image.parentElement?.querySelector('.dec-box-value');
        const ready = () => {
          image.classList.add('is-art-ready');
          price?.style.removeProperty('opacity');
        };
        const failed = () => {
          image.classList.remove('is-art-ready');
          // Keep the CSS case and live quote usable if artwork cannot load.
          if (price) price.style.opacity = '1';
        };
        image.classList.remove('is-art-ready');
        image.addEventListener('load', ready, { once: true });
        image.addEventListener('error', failed, { once: true });
        image.loading = 'eager';
        image.fetchPriority = 'high';
        if (image.complete) image.naturalWidth > 0 ? ready() : failed();
      }
      for (const node of clone.querySelectorAll('quest-objective-indicator, boon-product-indicator, .dec-box-quantity, input, .dec-visually-hidden, .dec-ticket-piece__copy')) {
        node.remove();
      }
      for (const node of [clone, ...clone.querySelectorAll('[data-bind], [id]')]) {
        node.removeAttribute('data-bind');
        node.removeAttribute('id');
      }
      clone.hidden = false;
      clone.setAttribute('data-dgx-piece', piece);
      if (kind === 'tickets') {
        // The popup owns its quote header. The main desk keeps its original
        // detached rate table and labels; only the artwork/control is cloned.
        const quote = document.createElement('span');
        quote.className = 'dgx-ticket-price';
        let price = quote;
        if (piece === 'foil') {
          const label = document.createElement('span');
          label.className = 'dec-price__kind';
          label.textContent = 'FOIL PACK';
          price = document.createElement('span');
          price.className = 'dgx-ticket-price__value';
          quote.append(label, price);
        }
        price.setAttribute('data-bind', `dgx-${piece}-price`);
        price.id = `dgx-${piece}-price`;
        clone.setAttribute('aria-describedby', price.id);
        clone.prepend(quote);
      }
      const target = kind === 'tickets' ? clone : clone.querySelector('.dec-box-card__add');
      if (target?.tagName === 'LABEL') {
        // The foil pack is a checkbox row on the desk; here it is a toggle button.
        const button = document.createElement('button');
        button.type = 'button';
        button.className = target.className;
        button.setAttribute('data-dgx-piece', piece);
        button.setAttribute('aria-label', source.getAttribute('aria-label') || 'Toggle one foil pack');
        button.setAttribute('aria-describedby', `dgx-${piece}-price`);
        button.append(...target.childNodes);
        this.#wirePiece(button, bind, piece);
        shelf.appendChild(button);
        continue;
      }
      if (target) this.#wirePiece(target, bind, piece);
      if (kind !== 'tickets') {
        const count = document.createElement('span');
        count.className = 'dgx-piece-count';
        count.setAttribute('data-dgx-count', piece);
        clone.appendChild(count);
      }
      shelf.appendChild(clone);
    }
  }

  #wirePiece(node, bind, piece) {
    node.addEventListener('click', () => {
      if (this.#dialogKind === 'luckboxes') this.#boxEditorKind = piece;
      if (piece === 'foil') this.#deskControl('dec-foil-check')?.click();
      else this.#deskControl(bind)?.click();
      if (piece === 'entry' || piece === 'ticket') this.#buildShelf('tickets'); // the desk rerolls its sample faces
      this.#syncFromDesk();
    });
    node.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      if (this.#dialogKind === 'luckboxes') this.#boxEditorKind = piece;
      if (piece === 'foil') {
        const check = this.#deskControl('dec-foil-check');
        if (check?.checked) check.click();
      } else {
        this.#deskControl(bind)?.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true }));
      }
      this.#syncFromDesk();
    });
  }

  #deskInput(name) { return this.#deskRoot()?.querySelector(`[name="${name}"]`) || null; }

  #writeDeskInput(name, value) {
    const input = this.#deskInput(name);
    if (!input || input.disabled) return;
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #boxCountInputName() {
    return this.#boxEditorKind === 'custom' ? 'dec-box-custom-count' : `dec-box-${this.#boxEditorKind}`;
  }

  #wireCustomBoxes(body) {
    const count = body.querySelector('[data-bind="dgx-custom-count"]');
    const size = body.querySelector('[data-bind="dgx-custom-eth"]');
    count?.addEventListener('input', () => {
      this.#writeDeskInput(this.#boxCountInputName(), count.value);
      this.#syncFromDesk();
    });
    const editPrice = () => {
      // Editing a preset's price converts that quantity to the desk's custom
      // kind. The desk still owns exclusivity, validation and the final quote.
      if (this.#boxEditorKind !== 'custom') {
        const previous = this.#boxCountInputName();
        const quantity = this.#deskInput(previous)?.value || '0';
        this.#boxEditorKind = 'custom';
        this.#writeDeskInput(previous, '0');
        this.#writeDeskInput('dec-box-custom-eth', size.value);
        this.#writeDeskInput('dec-box-custom-count', quantity);
      } else {
        this.#writeDeskInput('dec-box-custom-eth', size.value);
      }
      this.#syncFromDesk();
    };
    size?.addEventListener('input', editPrice);
    size?.addEventListener('change', editPrice);
    for (const step of body.querySelectorAll('[data-dgx-custom-step]')) {
      step.addEventListener('click', () => {
        const dir = step.getAttribute('data-dgx-custom-step');
        this.#deskRoot()?.querySelector(`[data-step-for="${this.#boxCountInputName()}"][data-dir="${dir}"]`)?.click();
        this.#syncFromDesk();
      });
    }
  }

  #syncFromDesk() {
    if (this.#dialogKind !== 'tickets' && this.#dialogKind !== 'luckboxes') return;
    const payment = this.#q('[data-bind="dgx-payment"]');
    const currencyControl = this.#deskControl('dec-funds-total-flip');
    const flipMode = currencyControl?.hidden === false
      && currencyControl.getAttribute('aria-pressed') === 'true';
    if (payment) {
      payment.hidden = currencyControl?.hidden !== false;
      const pending = this.#deskBuyPending && this.#deskControl('dec-buy-cta')?.disabled === true;
      for (const button of payment.querySelectorAll('[data-dgx-currency]')) {
        const selected = (button.getAttribute('data-dgx-currency') === 'flip') === flipMode;
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = payment.hidden || currencyControl?.disabled === true || pending;
      }
      // Foil packs only take ETH. Rebuild when the desk adds/removes the offer,
      // including when a window closes while this popup is already open.
      const foilAvailable = this.#deskControl('dec-foil-row')?.hidden === false;
      if (foilAvailable !== Boolean(this.#q('[data-dgx-piece="foil"]'))) this.#buildShelf('tickets');
    }
    const tickets = Number(this.#deskInput('dec-tickets')?.value || 0);
    const boxes = {
      small: Number(this.#deskInput('dec-box-small')?.value || 0),
      medium: Number(this.#deskInput('dec-box-medium')?.value || 0),
      large: Number(this.#deskInput('dec-box-large')?.value || 0),
      custom: Number(this.#deskInput('dec-box-custom-count')?.value || 0),
    };
    const foil = this.#deskControl('dec-foil-check')?.checked === true;
    const parts = [];
    if (tickets > 0) parts.push(`${tickets} TIX`);
    if (foil) parts.push('FOIL PACK');
    for (const [kind, count] of Object.entries(boxes)) {
      if (count > 0) parts.push(`${count} ${kind.toUpperCase()} LUCKBOX${count === 1 ? '' : 'ES'}`);
    }
    const order = this.#q('[data-bind="dgx-order"]');
    if (order) {
      order.textContent = '';
      const label = document.createElement('small');
      label.textContent = 'YOUR ORDER';
      const value = document.createElement('strong');
      value.textContent = parts.length ? parts.join(' + ') : 'NOTHING YET';
      order.append(label, value);
    }
    for (const [piece, count] of Object.entries(boxes)) {
      const card = this.#q(`[data-dgx-piece="${piece}"]`);
      card?.classList.toggle('is-selected', count > 0);
      const price = this.#deskControl(`dec-box-price-${piece}`)?.textContent;
      const cardPrice = card?.querySelector('.dec-box-value > strong');
      if (cardPrice && price != null) cardPrice.textContent = price;
      const cardUnit = card?.querySelector('.dec-box-value__unit');
      const priceUnit = this.#deskControl(`dec-box-price-${piece}-unit`);
      if (cardUnit && priceUnit) cardUnit.hidden = priceUnit.hidden;
      const badge = this.#q(`[data-dgx-count="${piece}"]`);
      if (badge) {
        badge.textContent = count > 0 ? `×${count}` : '';
        badge.hidden = count <= 0;
      }
    }
    this.#q('[data-dgx-piece="foil"]')?.classList.toggle('is-selected', foil);
    // Keep the chosen preset when its quantity reaches zero so the next +
    // restores the same size. CLEAR and reopening an empty order reset custom.
    this.#boxEditorKind = Object.keys(boxes).find(kind => boxes[kind] > 0) || this.#boxEditorKind;
    const kind = this.#boxEditorKind;
    const label = `${kind[0].toUpperCase()}${kind.slice(1)} Luckbox`;
    const kindLabel = this.#q('[data-bind="dgx-box-kind"]');
    if (kindLabel) kindLabel.textContent = kind.toUpperCase();
    const customCount = this.#q('[data-bind="dgx-custom-count"]');
    if (customCount) {
      customCount.setAttribute('aria-label', `${label} quantity`);
      if (document.activeElement !== customCount) customCount.value = this.#deskInput(this.#boxCountInputName())?.value || '0';
    }
    const customEth = this.#q('[data-bind="dgx-custom-eth"]');
    if (customEth) {
      customEth.setAttribute('aria-label', `${label} ETH per box`);
      customEth.title = kind === 'custom' ? '' : 'Edit the price to make a custom Luckbox.';
      const source = this.#deskInput('dec-box-custom-eth');
      customEth.step = source?.step || '0.001';
      customEth.max = source?.max || '';
      if (document.activeElement !== customEth) {
        customEth.value = kind === 'custom' ? (source?.value ?? '0.01')
          : (this.#deskControl(`dec-box-price-${kind}`)?.textContent?.trim() || '').replaceAll(',', '');
        customEth.placeholder = '—';
      }
    }
    for (const step of this.#qa('[data-dgx-custom-step]')) {
      step.setAttribute('aria-label', `${step.getAttribute('data-dgx-custom-step') === '1' ? 'Add' : 'Remove'} one ${kind} Luckbox`);
    }

    const bonus = this.#q('[data-bind="dgx-bonus"]');
    for (const kind of ['boon', 'quest']) {
      const source = this.#deskControl(`dec-purchase-${kind}-effect`);
      const target = this.#q(`[data-bind="dgx-${kind}-effect"]`);
      if (!target) continue;
      target.hidden = !parts.length || source?.hidden !== false;
      target.textContent = target.hidden ? '' : source.textContent;
    }
    if (bonus) {
      const luckboxes = this.#dialogKind === 'luckboxes';
      // The desk's Luckbox preview already hides zero and unavailable quotes.
      // Mirror that exact state instead of presenting an unknown quote as zero.
      const source = this.#deskControl(luckboxes ? 'dec-custom-box-bonus-amount' : 'dec-flip-credit-total');
      const positiveQuote = this.#deskControl('dec-custom-box-bonus');
      bonus.closest('.dgx-bonus').hidden = flipMode || (luckboxes && positiveQuote?.hidden !== false);
      bonus.textContent = luckboxes ? source?.textContent || ''
        : source?.textContent?.startsWith('+') ? source.textContent : '+0 FLIP';
    }
    for (const kind of ['entry', 'ticket', 'pack', 'foil']) {
      const target = this.#q(`[data-bind="dgx-${kind}-price"]`);
      const source = this.#deskControl(`dec-${kind}-price`);
      if (target && source) {
        if (kind === 'foil') {
          const quote = purchasePopupQuote(source.textContent || '—');
          const unitAt = quote.lastIndexOf(' ');
          const amount = document.createElement('span');
          amount.className = 'dec-price__amount';
          amount.textContent = unitAt < 0 ? quote : quote.slice(0, unitAt);
          const unit = document.createElement('span');
          unit.className = 'dec-price__unit';
          unit.textContent = unitAt < 0 ? '' : quote.slice(unitAt);
          target.replaceChildren(amount, unit);
        } else {
          target.replaceChildren(...[...source.childNodes].map(node => node.cloneNode(true)));
          const amount = target.querySelector('.dec-price__amount');
          if (amount && target.querySelector('.dec-price__unit')?.textContent.trim() === 'ETH') {
            amount.textContent = purchasePopupQuote(`${amount.textContent} ETH`).slice(0, -4);
          }
        }
        target.setAttribute('aria-label', purchasePopupQuote(source.getAttribute('aria-label') || source.textContent));
      }
    }
    const cta = this.#deskControl('dec-buy-cta');
    const action = this.#deskControl('dec-buy-cta-action')?.textContent?.trim() || 'BUY';
    const amountEl = this.#deskControl('dec-buy-cta-amount');
    const amount = amountEl && !amountEl.hidden ? purchasePopupQuote(amountEl.textContent.trim()) : '';
    const confirm = this.#q('[data-bind="dgx-confirm"]');
    if (confirm) {
      const empty = !parts.length;
      confirm.textContent = empty ? 'ADD SOMETHING' : [action, amount].filter(Boolean).join(' ');
      confirm.disabled = empty || cta?.disabled === true;
    }
    const error = this.#deskControl('dec-error');
    const message = error && !error.hidden ? error.textContent.trim() : '';
    this.#setState(message, message ? 'error' : '');
  }

  // ---- Degenesis box ------------------------------------------------------------

  #renderDegenesisBody({ keepAmount = false } = {}) {
    const lb = this.#lootbox;
    const { unlocked, lock: lockReason, creditWei } = this.#model;
    this.#setHero({
      title: 'DEGENESIS BOX',
      copy: unlocked
        ? 'Protocol coins, delivered next game day.'
        : lockReason === 'opens-tomorrow'
          ? 'The Degenesis sale opens tomorrow. Buy tickets, Luckboxes or passes today to earn credit.'
          : lockReason === 'connect'
            ? 'Connect a wallet to see your Degenesis credit.'
            : 'Every ETH you spend on tickets, Luckboxes, passes and foil packs earns 25% back as Degenesis credit.',
      sigil: DEGENESIS_BOX_ART,
    });
    this.#q('[data-bind="dgx-clear"]').hidden = true;
    const body = this.#q('[data-bind="dgx-body"]');
    const prior = keepAmount ? body.querySelector('[data-bind="dgx-amount"]')?.value : null;
    const maxWei = lb && creditWei != null ? degenesisMaxWei(creditWei, lb.DEGENESIS_UNIT_WEI) : 0n;
    body.innerHTML = `
      <div class="dgx-credit">
        <small>YOU CAN BUY</small>
        <strong>${creditWei != null ? `${ethText(maxWei)} ETH` : '—'}</strong>
      </div>
      <label class="qst-action-adjust dgx-amount" ${unlocked ? '' : 'hidden'}>
        <span class="qst-action-dgn__field-head">
          <span>AMOUNT</span>
          <small>MIN ${ethText(lb?.DEGENESIS_MIN_WEI ?? 0n)} ETH</small>
        </span>
        <span class="qst-action-dgn__stepper qst-action-adjust__stepper">
          <input type="number" data-bind="dgx-amount" min="0" step="0.0001"
                 inputmode="decimal" aria-label="Degenesis box amount in ETH">
          <strong>ETH</strong>
          <button type="button" data-bind="dgx-max" class="dgx-max">MAX</button>
        </span>
      </label>
      <div class="dgx-boon-chance" data-bind="dgx-boon-chance" ${unlocked ? '' : 'hidden'}
           title="Boon draws, up to 3 per box. Duplicate, weaker or unavailable boons may not be kept.">
        <small>BOON CHANCE</small><strong data-bind="dgx-boon-odds">—</strong>
      </div>
    `;
    const amount = body.querySelector('[data-bind="dgx-amount"]');
    if (amount) {
      amount.value = prior ?? ethText(maxWei);
      amount.addEventListener('input', () => this.#syncDegenesisConfirm());
    }
    body.querySelector('[data-bind="dgx-max"]')?.addEventListener('click', () => {
      if (amount) amount.value = ethText(maxWei);
      this.#syncDegenesisConfirm();
    });
    this.#syncDegenesisConfirm();
  }

  #degenesisAmount() {
    const lb = this.#lootbox;
    const input = this.#q('[data-bind="dgx-amount"]');
    if (!lb || !input) return null;
    return degenesisAmountWei(input.value, lb.DEGENESIS_UNIT_WEI);
  }

  #syncDegenesisConfirm() {
    const confirm = this.#q('[data-bind="dgx-confirm"]');
    if (!confirm) return;
    const { unlocked, lock: lockReason, creditWei } = this.#model;
    if (!unlocked) {
      confirm.textContent = LOCK_COPY[lockReason] || 'LOCKED';
      confirm.disabled = true;
      return;
    }
    const input = this.#q('[data-bind="dgx-amount"]');
    const typed = String(input?.value ?? '').trim();
    const amount = this.#degenesisAmount();
    const odds = this.#q('[data-bind="dgx-boon-odds"]');
    if (odds) odds.textContent = degenesisBoonChanceLabel(degenesisBoonOdds(amount));
    // Mirrors purchaseDegenesisBox: >= DEGENESIS_MIN, whole DEGENESIS_UNIT steps, <= credit.
    const error = amount == null
      ? (typed && typed !== '0' ? 'Degenesis boxes are sized in 0.0001 ETH steps.' : null)
      : amount === 0n ? null : this.#lootbox.degenesisAmountError(amount, creditWei);
    const valid = amount != null && amount > 0n && error == null && creditWei != null;
    confirm.disabled = !valid || this.#busy;
    confirm.textContent = this.#busy ? 'BUYING…' : valid ? `BUY ${ethText(amount)} ETH` : 'ENTER AN AMOUNT';
    if (error) this.#setState(error, 'error');
    else if (!this.#busy) this.#setState('');
  }

  async #buyDegenesis() {
    const amount = this.#degenesisAmount();
    if (this.#busy || amount == null || !this.#model.unlocked) return;
    const lb = this.#lootbox;
    this.#busy = true;
    this.#syncDegenesisConfirm();
    this.#setState('Confirm in your wallet…', 'submitting');
    try {
      await lb.purchaseDegenesisBox({
        amountWei: amount,
        player: getActingAddress(),
        preferClaimable: lb.readPurchaseFundingPriority() === 'claimable',
        useAfking: lb.readPurchaseUseAfking(),
        onSubmitted: () => this.#setState('Buying your Degenesis box…', 'submitting'),
      });
      this.#busy = false;
      const opener = this.#returnFocus;
      this.#close({ restoreFocus: false });
      this.#showPurchaseReceipt(amount, opener);
      void this.#refreshCredit();
    } catch (error) {
      this.#busy = false;
      this.#syncDegenesisConfirm();
      this.#setState(error?.userMessage || error?.shortMessage || error?.message || 'Purchase failed.', 'error');
    } finally {
      this.#busy = false;
    }
  }

  #showPurchaseReceipt(amount, opener) {
    if (!this.isConnected) return;
    if (!this.#receiptEl) {
      const receipt = document.createElement('dialog');
      receipt.className = 'dgx-receipt';
      receipt.setAttribute('aria-labelledby', 'dgx-receipt-title');
      receipt.setAttribute('aria-describedby', 'dgx-receipt-copy');
      receipt.innerHTML = `
        <h2 id="dgx-receipt-title">Box purchased</h2>
        <strong class="dgx-receipt__amount" data-bind="dgx-receipt-amount"></strong>
        <p id="dgx-receipt-copy">Arrives next game day.</p>
        <button type="button" data-bind="dgx-receipt-done" autofocus>OK</button>`;
      receipt.querySelector('[data-bind="dgx-receipt-done"]').addEventListener('click', () => receipt.close());
      receipt.addEventListener('close', () => {
        unlock();
        const trigger = this.#receiptReturnFocus;
        this.#receiptReturnFocus = null;
        try { trigger?.focus({ preventScroll: true }); } catch (_e) { /* optional focus restoration */ }
      });
      document.body.appendChild(receipt);
      this.#receiptEl = receipt;
    }
    this.#receiptReturnFocus = opener;
    this.#receiptEl.querySelector('[data-bind="dgx-receipt-amount"]').textContent = `${ethText(amount)} ETH`;
    lock();
    this.#receiptEl.showModal();
  }
}

// Rendered after the class body so the summary helper stays a plain export for tests.
AppDegenesisHub.prototype.renderOrdersInto = function renderOrdersInto(list, orders) {
  list.replaceChildren(...orders.slice(0, 12).map((order) => {
    const li = document.createElement('li');
    li.className = order.resolved ? 'is-settled' : 'is-pending';
    const head = document.createElement('strong');
    head.textContent = `DAY ${order.degenesisDay} · ${ethText(order.amountRawWei)} ETH`;
    const body = document.createElement('span');
    // /degenesis/orders rows are token wei (18 decimals) for FLIP, WWXRP and sDGNRS alike.
    const whole = (v) => (BigInt(v) / 10n ** 18n).toLocaleString('en-US');
    body.textContent = degenesisOrderSummary(order, {
      flip: whole,
      dgnrs: (v) => trimDisplayZeros(displayToken(BigInt(v), 2)),
      wwxrp: whole,
    });
    li.append(head, body);
    return li;
  }));
};

if (typeof customElements !== 'undefined' && !customElements.get('app-degenesis-hub')) {
  customElements.define('app-degenesis-hub', AppDegenesisHub);
}

export { AppDegenesisHub };
